import type { PayloadAction } from '@reduxjs/toolkit';
import { AddBenefitAdjustmentRequestBody } from '@/domains/payments/features/paymentModals/modals/adjustments/types/requests/AddBenefitAdjustmentRequestBody';
import { AddPaymentAdjustmentRequestBody } from '@/domains/payments/features/paymentModals/modals/adjustments/types/requests/AddPaymentAdjustmentRequestBody';
import { call, put, takeLatest } from 'redux-saga/effects';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getBenefitSectionDetails } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import { getPayments } from '@/domains/payments/features/PaymentsSection/store/payments.slice';
import { BenefitAdjustmentEvents } from '@/domains/payments/features/paymentModals/modals/adjustments/events';
import {
  addAdjustmentError,
  addAdjustmentSuccess,
  addBenefitAdjustment,
  addPaymentAdjustment,
} from '@/domains/payments/features/paymentModals/modals/adjustments/store/addAdjustment.slice';
import PaymentFormEvent from '@/domains/payments/features/paymentModals/shared/events';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function* addPaymentAdjustmentSaga({ payload }: PayloadAction<AddPaymentAdjustmentRequestBody & { claimId: number }>) {
  try {
    const { claimId, ...body } = payload;
    yield call(HttpService.post, '/PaymentAdjustment', body, {
      baseURL: import.meta.env.VITE_API_URL,
    });

    yield put(addAdjustmentSuccess());
    yield put(getPayments({ claimId }));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Adjustment has been added successfully',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
    yield put(addAdjustmentError());
  } finally {
    ModalService.close(Modals.AddPaymentAdjustment);
    EventService.dispatch(new PaymentFormEvent.CreateRequestSuccess({}));
  }
}

function* addBenefitAdjustmentSaga({ payload }: PayloadAction<AddBenefitAdjustmentRequestBody & { claimId: number }>) {
  try {
    const { claimId, ...body } = payload;
    yield call(HttpService.post, '/examiner/benefit-adjustment', body, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    yield put(addAdjustmentSuccess());
    yield put(getBenefitSectionDetails(String(claimId)));

    EventService.dispatch(new BenefitAdjustmentEvents.BenefitAdjustmentCreated({ claimId }));

    SnacksService.add({
      type: SnackType.Success,
      title: 'Adjustment has been added successfully',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
    yield put(addAdjustmentError());
  } finally {
    ModalService.close(Modals.AddBenefitAdjustment);
    EventService.dispatch(new PaymentFormEvent.CreateRequestSuccess({}));
  }
}

export function* addAdjustmentWatcher() {
  yield takeLatest([addPaymentAdjustment.type], addPaymentAdjustmentSaga);
  yield takeLatest([addBenefitAdjustment.type], addBenefitAdjustmentSaga);
}
